import { type FC } from "react"
import styled from "styled-components"

import Button from "~/components/Button"
import Checkbox from "~/components/Checkbox"
import InputLabel from "~/components/InputLabel"
import { useAlert } from "~/contexts/AlertContext"
import { useToast } from "~/contexts/ToastContext"
import { usePlatform, useUser } from "~/contexts/UserContext"
import trpc, { query } from "~/utilities/trpc"

import GamificationLevels from "./GamificationLevels"

const CommunitySettings: FC = () => {
	const user = useUser()
	const platform = usePlatform()!
	const alert = useAlert()
	const toast = useToast()

	const update = query.platform.update.useMutation({
		async onSuccess() {
			await user.reload()
			toast.showChangesSaved("Community")
		},
		onError(error) {
			console.error(error)
			toast.showError("Failed to update community settings. Please try again.")
		},
	})

	async function handleDeleteCommunity() {
		const dialog = await alert.confirm("Delete community", "Are you sure you want to delete the community?")
		if (!dialog.result) return
		await trpc.platform.update.mutate({ isCommunityEnabled: false })
		await user.reload()
		dialog.close()
	}

	return (
		<Container>
			<div className="flex max-w-2xl flex-col gap-2">
				<Checkbox
					label="Screen member posts and comments before publishing"
					isChecked={platform.community?.isCommunityModerationEnabled ?? true}
					onChange={isCommunityModerationEnabled => update.mutate({ isCommunityModerationEnabled })}
					isLoading={update.isPending && update.variables?.isCommunityModerationEnabled !== undefined}
				/>
				<p className="text-sm text-gray-500">
					AI checks text for harassment, hate speech, sexual content, violence, self-harm and illicit
					instructions. Flagged or elevated-risk submissions and media wait in the Moderation tab for your
					approval. If automatic screening fails, the submission is published. This filter does not check for
					general spam or privacy breaches. Creator submissions publish immediately. Turning this off keeps
					existing submissions awaiting review.
				</p>
			</div>
			<Checkbox
				label="Allow members to message each other"
				isChecked={platform.community?.isMemberMessagingEnabled ?? false}
				onChange={isMemberMessagingEnabled => update.mutate({ isMemberMessagingEnabled })}
				isLoading={update.isPending && update.variables?.isMemberMessagingEnabled !== undefined}
			/>
			<Checkbox
				label="Enable gamification"
				isChecked={platform.community?.isGamificationEnabled ?? false}
				onChange={isGamificationEnabled => update.mutate({ isGamificationEnabled })}
				isLoading={update.isPending && update.variables?.isGamificationEnabled !== undefined}
			/>
			<GamificationLevels />
			<div>
				<InputLabel>Danger zone</InputLabel>
				<Button variant="primary-danger" onClick={handleDeleteCommunity}>
					Delete community
				</Button>
			</div>
		</Container>
	)
}

const Container = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 16px;
`

export default CommunitySettings
